import { useContext, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import CheckRounded from '@mui/icons-material/CheckRounded';
import InsightsRounded from '@mui/icons-material/InsightsRounded';
import LockOutlined from '@mui/icons-material/LockOutlined';
import ScheduleRounded from '@mui/icons-material/ScheduleRounded';
import BrandHeader from '../../components/BrandHeader';
import heroImage from '../../images/digitalwerks-survey-hero.png';
import { AppVarsContext } from '../../context/AppVarsContext';

const surveyLeadConfig = {
    segmentId: 27,
    contactEmailId: 82,
    responderEmailId: 83,
};

const initialForm = {
    name: '',
    email: '',
    company: '',
    phone: '',
    details: '',
};

const principles = [
    { icon: <ScheduleRounded />, title: 'Designed to be brief', copy: 'Clear questions, visible progress, and no unnecessary steps.' },
    { icon: <LockOutlined />, title: 'Handled with care', copy: 'Your responses are collected only for the purpose explained in your invitation.' },
    { icon: <InsightsRounded />, title: 'Built for action', copy: 'Feedback is organized into signals teams can use to make the next decision.' },
];

const Homepage = () => {
    const { appVars, appLoading } = useContext(AppVarsContext);
    const [formState, setFormState] = useState(initialForm);
    const [submitState, setSubmitState] = useState({ status: 'idle', message: '' });

    const handleChange = ({ target: { name, value } }) => {
        setFormState((current) => ({ ...current, [name]: value }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!appVars?.api_url) {
            setSubmitState({
                status: 'error',
                message: 'The survey setup service is not available right now. Please try again in a moment.',
            });
            return;
        }

        const trimmed = {
            name: formState.name.trim(),
            email: formState.email.trim(),
            company: formState.company.trim(),
            phone: formState.phone.trim(),
            details: formState.details.trim(),
        };

        if (!trimmed.name || !trimmed.email || !trimmed.details) {
            setSubmitState({
                status: 'error',
                message: 'Please add your name, email, and what you want the survey to accomplish.',
            });
            return;
        }

        setSubmitState({ status: 'submitting', message: '' });

        const submittedAt = new Date().toLocaleString('en-US', {
            dateStyle: 'medium',
            timeStyle: 'short',
        });

        const domain = (() => {
            try {
                return new URL(appVars.applicationURL || window.location.href).hostname;
            } catch (error) {
                return window.location.hostname;
            }
        })();

        const payload = {
            name: trimmed.name,
            email: trimmed.email,
            company: trimmed.company,
            phone: trimmed.phone,
            project_details: trimmed.details,
            submitted_at: submittedAt,
            source: 'Survey setup request',
            page_url: window.location.href,
            domain,
            form_name: 'Survey setup request',
            inquiry_type: 'survey_setup',
            segment_id: surveyLeadConfig.segmentId,
            segmentId: surveyLeadConfig.segmentId,
            contact_email_id: surveyLeadConfig.contactEmailId,
            contactTemplateId: surveyLeadConfig.contactEmailId,
            contact_template_id: surveyLeadConfig.contactEmailId,
            responder_email_id: surveyLeadConfig.responderEmailId,
            responderTemplateId: surveyLeadConfig.responderEmailId,
            responder_template_id: surveyLeadConfig.responderEmailId,
        };

        try {
            const response = await fetch(`${appVars.api_url.replace(/\/$/, '')}/mautic/lead`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                throw new Error(`Lead capture failed with status ${response.status}`);
            }

            setFormState(initialForm);
            setSubmitState({
                status: 'success',
                message: 'Thanks. Your survey setup request was sent and someone will follow up soon.',
            });
        } catch (error) {
            console.error(error);
            setSubmitState({
                status: 'error',
                message: 'We could not send your request just now. Please try again in a moment.',
            });
        }
    };

    return (
        <Box sx={{ minHeight: '100vh', overflow: 'hidden', bgcolor: 'background.default' }}>
            <BrandHeader />

            <Box component="main">
                <Box
                    sx={{
                        width: 'min(1200px, calc(100% - 40px))',
                        mx: 'auto',
                        pt: { xs: 4, md: 7 },
                        pb: { xs: 7, md: 11 },
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, .88fr) minmax(520px, 1.12fr)' },
                        alignItems: 'center',
                        gap: { xs: 5, lg: 8 },
                    }}
                >
                    <Stack spacing={3.25} sx={{ position: 'relative', zIndex: 2 }}>
                        <Typography variant="overline" sx={{ color: 'primary.dark' }}>
                            Survey platform
                        </Typography>
                        <Typography variant="h1" sx={{ maxWidth: 720 }}>
                            Better answers move good work <Box component="span" sx={{ color: 'primary.main' }}>forward.</Box>
                        </Typography>
                        <Typography variant="h6" component="p" color="text.secondary" sx={{ maxWidth: 620, fontWeight: 400, lineHeight: 1.65 }}>
                            Give people a clean, low-friction way to respond. The experience stays simple for respondents and useful for the team reading the results.
                        </Typography>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
                            <Button href="#survey-setup" endIcon={<ArrowForwardRounded />} size="large">
                                Start a survey setup
                            </Button>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ color: 'text.secondary' }}>
                                <CheckRounded sx={{ color: 'secondary.main', fontSize: 20 }} />
                                <Typography variant="body2">White-label respondent experience</Typography>
                            </Stack>
                        </Stack>
                    </Stack>

                    <Box sx={{ position: 'relative', minHeight: { xs: 360, md: 520 } }}>
                        <Box
                            sx={{
                                position: 'absolute',
                                inset: { xs: '20px -90px -20px 32px', md: '-10px -160px -30px 50px' },
                                borderRadius: '50%',
                                background: 'radial-gradient(circle, rgba(0,156,222,.18), rgba(0,155,139,.06) 46%, transparent 70%)',
                            }}
                        />
                        <Box
                            component="figure"
                            sx={{
                                m: 0,
                                height: '100%',
                                minHeight: { xs: 360, md: 520 },
                                borderRadius: { xs: '24px 24px 80px 24px', md: '32px 32px 120px 32px' },
                                overflow: 'hidden',
                                position: 'relative',
                                boxShadow: '0 28px 70px rgba(11,36,56,.16)',
                            }}
                        >
                            <Box component="img" src={heroImage} alt="A digital strategy team reviewing survey feedback together" sx={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: '64% center', display: 'block' }} />
                            <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, rgba(11,36,56,.12), transparent 55%)' }} />
                            <Box
                                sx={{
                                    position: 'absolute',
                                    left: { xs: 18, md: 28 },
                                    bottom: { xs: 18, md: 28 },
                                    p: 2,
                                    borderRadius: 3,
                                    bgcolor: 'rgba(255,255,255,.92)',
                                    backdropFilter: 'blur(12px)',
                                    boxShadow: '0 12px 32px rgba(11,36,56,.16)',
                                }}
                            >
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>THE SIGNAL</Typography>
                                <Stack direction="row" spacing={0.75} alignItems="flex-end" sx={{ mt: 1, height: 34 }}>
                                    {[12, 20, 16, 30, 24, 34, 28].map((height, index) => (
                                        <Box key={index} sx={{ width: 8, height, borderRadius: 4, bgcolor: index === 5 ? 'warning.main' : index > 3 ? 'secondary.main' : 'primary.main' }} />
                                    ))}
                                </Stack>
                            </Box>
                        </Box>
                    </Box>
                </Box>

                <Box id="how-it-works" sx={{ bgcolor: 'primary.dark', color: 'common.white', py: { xs: 8, md: 12 } }}>
                    <Box sx={{ width: 'min(1120px, calc(100% - 40px))', mx: 'auto' }}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '.8fr 1.2fr' }, gap: { xs: 4, md: 10 } }}>
                            <Box>
                                <Chip label="A clearer feedback loop" sx={{ bgcolor: 'rgba(0,156,222,.16)', color: '#9edfff', fontWeight: 700, mb: 3 }} />
                                <Typography variant="h2" sx={{ color: 'common.white', maxWidth: 480 }}>
                                    A small ask. A useful signal.
                                </Typography>
                            </Box>
                            <Stack spacing={2.25}>
                                {principles.map(({ icon, title, copy }, index) => (
                                    <Box key={title} sx={{ display: 'grid', gridTemplateColumns: '52px 1fr', gap: 2, py: 2.5, borderBottom: index < principles.length - 1 ? '1px solid rgba(255,255,255,.14)' : 0 }}>
                                        <Box sx={{ width: 46, height: 46, borderRadius: 2.5, display: 'grid', placeItems: 'center', bgcolor: index === 1 ? 'rgba(245,154,35,.16)' : 'rgba(0,155,139,.18)', color: index === 1 ? '#ffbf64' : '#63d4c8' }}>{icon}</Box>
                                        <Box>
                                            <Typography variant="h5" sx={{ color: 'common.white', mb: 0.75 }}>{title}</Typography>
                                            <Typography sx={{ color: 'rgba(255,255,255,.68)', maxWidth: 620, lineHeight: 1.7 }}>{copy}</Typography>
                                        </Box>
                                    </Box>
                                ))}
                            </Stack>
                        </Box>
                    </Box>
                </Box>

                <Box id="survey-setup" sx={{ py: { xs: 8, md: 11 } }}>
                    <Box
                        sx={{
                            width: 'min(1120px, calc(100% - 40px))',
                            mx: 'auto',
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, .9fr) minmax(420px, 1.1fr)' },
                            gap: { xs: 4, md: 6 },
                            alignItems: 'start',
                        }}
                    >
                        <Stack spacing={2.5}>
                            <Chip label="Survey setup" sx={{ width: 'fit-content', bgcolor: 'rgba(0,156,222,.1)', color: 'primary.dark', fontWeight: 700 }} />
                            <Typography variant="h2" sx={{ maxWidth: 540 }}>
                                Tell us what you want the survey to do.
                            </Typography>
                            <Typography color="text.secondary" sx={{ maxWidth: 580, lineHeight: 1.8 }}>
                                Share the audience, the goal, and any timing or delivery details. We will use that to shape the next step and follow up with the right setup questions.
                            </Typography>
                            <Stack spacing={1.5} sx={{ color: 'text.secondary' }}>
                                <Typography variant="body2">Clear follow-up for internal teams</Typography>
                                <Typography variant="body2">Responder confirmation email included</Typography>
                                <Typography variant="body2">White-label front-end experience</Typography>
                            </Stack>
                        </Stack>

                        <Box
                            component="form"
                            onSubmit={handleSubmit}
                            sx={{
                                p: { xs: 2.5, md: 3.5 },
                                borderRadius: 4,
                                bgcolor: 'common.white',
                                border: '1px solid rgba(20,43,60,.08)',
                                boxShadow: '0 22px 54px rgba(11,36,56,.1)',
                            }}
                        >
                            <Stack spacing={2}>
                                <Typography variant="h5">Request survey setup</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    We will use this to route the right follow-up and send a confirmation to the requester.
                                </Typography>

                                <TextField
                                    label="Name"
                                    name="name"
                                    value={formState.name}
                                    onChange={handleChange}
                                    required
                                    fullWidth
                                />
                                <TextField
                                    label="Email"
                                    name="email"
                                    type="email"
                                    value={formState.email}
                                    onChange={handleChange}
                                    required
                                    fullWidth
                                />
                                <TextField
                                    label="Company or team"
                                    name="company"
                                    value={formState.company}
                                    onChange={handleChange}
                                    fullWidth
                                />
                                <TextField
                                    label="Phone"
                                    name="phone"
                                    value={formState.phone}
                                    onChange={handleChange}
                                    fullWidth
                                />
                                <TextField
                                    label="What should the survey help you learn?"
                                    name="details"
                                    value={formState.details}
                                    onChange={handleChange}
                                    required
                                    fullWidth
                                    multiline
                                    minRows={5}
                                    placeholder="Audience, survey goal, timing, delivery details, or anything else that would help."
                                />

                                {submitState.status === 'success' ? <Alert severity="success">{submitState.message}</Alert> : null}
                                {submitState.status === 'error' ? <Alert severity="error">{submitState.message}</Alert> : null}

                                <Button type="submit" size="large" disabled={submitState.status === 'submitting' || appLoading}>
                                    {submitState.status === 'submitting' ? (
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <CircularProgress size={18} color="inherit" />
                                            <span>Sending request</span>
                                        </Stack>
                                    ) : 'Send request'}
                                </Button>
                            </Stack>
                        </Box>
                    </Box>
                </Box>
            </Box>

            <Box component="footer" sx={{ py: 3, px: 2.5, textAlign: 'center', bgcolor: 'primary.dark', borderTop: '1px solid rgba(255,255,255,.1)' }}>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,.56)' }}>Provided by DigitalWerks Innovations.</Typography>
            </Box>
        </Box>
    );
};

export default Homepage;
